import * as J from "react";
import { useState as ee, useEffect as O, createRef as Ie, createContext as We, useRef as F, useLayoutEffect as $e, useMemo as L, Suspense as Ue, useContext as Y, useCallback as Ze } from "react";
import { jsxs as Ce, jsx as W, Fragment as Oe } from "react/jsx-runtime";
import * as B from "three";
import { useFrame as te, useThree as me } from "@react-three/fiber";
import { emitEvent as k, useEvent as $ } from "@utsubo/events";
import { computeBoundsTree as He, disposeBoundsTree as Ge, acceleratedRaycast as Xe, SAH as Je, MeshBVH as et, MeshBVHVisualizer as tt } from "three-mesh-bvh";
import * as xe from "comlink";
import { create as Se } from "zustand";
import { shallow as Fe } from "zustand/shallow";
import { mergeBufferGeometries as rt } from "three/addons/utils/BufferGeometryUtils.js";
function Re() {
  const [e, t] = /* @__PURE__ */ ee(typeof document > "u" || !document.hidden || document.visibilityState !== "hidden");
  return O(() => {
    const r = () => t(!document.hidden);
    return document.addEventListener("visibilitychange", r), () => {
      document.removeEventListener("visibilitychange", r);
    };
  }, []), e;
}
function Te(e) {
  return typeof e == "symbol" ? false : !isNaN(e);
}
function Ee(e, t) {
  let r = e - e % t;
  return e % t != 0 && (r += t), r;
}
function nt(e) {
  return Math.max(...Object.values(e).map((t) => {
    var r;
    return (r = t.align) !== null && r !== void 0 ? r : 1;
  }));
}
function re(e, t, { byteOffset: r = 0, length: i = 0, align: o = nt(t) } = {}) {
  var c;
  const u = new DataView(e, r);
  let s = 0;
  const l = /* @__PURE__ */ Object.assign({}, t);
  for (const [h, g] of Object.entries(l))
    l[h] = Object.assign(Object.assign({}, g), { offset: Ee(s, (c = g.align) !== null && c !== void 0 ? c : 1) }), s = l[h].offset + g.size;
  return s = Ee(s, o), i || (i = Math.floor((e.byteLength - r) / s)), new Proxy(new Array(i), { has(h, g) {
    return Te(g) ? g < i : g === "buffer" ? true : g in h;
  }, get(h, g, f) {
    if (g === "buffer")
      return e;
    if (!Te(g)) {
      let m = h[g];
      return typeof m == "function" && (m = m.bind(f)), m;
    }
    const y = /* @__PURE__ */ parseInt(g), d = y * s;
    if (!(y >= h.length)) {
      if (!h[y]) {
        h[y] = {};
        for (const [m, n] of Object.entries(l))
          "get" in n && Object.defineProperty(h[y], m, { enumerable: true, get() {
            return n.get(u, d + n.offset);
          }, set(x) {
            return n.set(u, d + n.offset, x);
          } });
        Object.freeze(h[y]);
      }
      return h[y];
    }
  } });
}
function ze(e, t, { byteOffset: r = 0, align: i = 1 } = {}) {
  return re(e, t, { byteOffset: r, align: i })[0];
}
function Me({ endianness: e = "little", align: t = 2 } = {}) {
  if (e !== "big" && e !== "little")
    throw Error("Endianness needs to be either 'big' or 'little'");
  const r = e === "little";
  return { align: t, size: Int16Array.BYTES_PER_ELEMENT, get: (i, o) => i.getInt16(o, r), set: (i, o, c) => i.setInt16(o, c, r) };
}
function a({ endianness: e = "little", align: t = 4 } = {}) {
  if (e !== "big" && e !== "little")
    throw Error("Endianness needs to be either 'big' or 'little'");
  const r = e === "little";
  return { align: t, size: Float32Array.BYTES_PER_ELEMENT, get: (i, o) => i.getFloat32(o, r), set: (i, o, c) => i.setFloat32(o, c, r) };
}
function E({ endianness: e = "little", align: t = 8 } = {}) {
  if (e !== "big" && e !== "little")
    throw Error("Endianness needs to be either 'big' or 'little'");
  const r = e === "little";
  return { align: t, size: Float64Array.BYTES_PER_ELEMENT, get: (i, o) => i.getFloat64(o, r), set: (i, o, c) => i.setFloat64(o, c, r) };
}
function A() {
  return { align: 1, size: 1, get: (e, t) => e.getInt8(t), set: (e, t, r) => e.setInt8(t, r) };
}
const ke = { bodies: { dynamic: [], kinematic: [], peerKinematic: [], inReach: [], static: [], zone: [], orderedKinematic: [] }, staticBodiesGroups: /* @__PURE__ */ new Map(), apis: { dynamic: [], kinematic: [], peerKinematic: [], inReach: [], static: [], zone: [] }, physicLoop: null, config: { steps: 10, referenceFps: 60, throttledFps: 60, maxSubStep: 60 }, sharedDynamic: null, forceUpdateCamera: false, tempSharedDynamic: null, sharedStatic: null, sharedKinematic: null, sharedPeerKinematic: null, sharedCamera: null, sharedZone: null, sharedInReach: null, sharedPhysic: null, sharedPointer: null, indexInArrayOfKinematicThatPointerCollidedWith: null, callBacks: [], callBacksReady: [], clock: new B.Clock(false), accumulator: 0, lastTimeClock: 0, totalTimeAtStep: 0, quaternionTotalY: new B.Quaternion(), camera: new B.PerspectiveCamera(), collidingStateOnTop: { static: /* @__PURE__ */ new Map(), kinematic: /* @__PURE__ */ new Map(), peerKinematic: /* @__PURE__ */ new Map() }, newCollidingState: { static: {}, kinematic: {}, peerKinematic: {} }, collidingState: { static: {}, kinematic: {}, peerKinematic: {} }, stoppedCollidingState: { static: {}, kinematic: {}, peerKinematic: {} }, newMouseOverState: { static: {}, kinematic: {}, peerKinematic: {} }, mouseOverState: { static: {}, kinematic: {}, peerKinematic: {} }, stoppedMouseOverState: { static: {}, kinematic: {}, peerKinematic: {} }, newCameraIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, cameraIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, stoppedCameraIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, newPointerIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, pointerIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, stoppedPointerIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, binderOnSit: false, binderOnCollide: { static: {}, kinematic: {}, peerKinematic: {} }, binderOnClick: { static: {}, kinematic: {}, peerKinematic: {} }, binderOnMouseOver: { static: {}, kinematic: {}, peerKinematic: {} }, binderOnMouseLeave: { static: {}, kinematic: {}, peerKinematic: {} }, binderOnStopCollide: { static: {}, kinematic: {}, peerKinematic: {} }, binderOnCameraIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, binderOnPointerIntersect: { static: {}, kinematic: {}, peerKinematic: {} }, binderReach: {}, binderOutOfReach: {}, binderEnter: {}, binderLeave: {}, paths: {} };
ke.staticBodiesGroups.set("large", []);
const _e = (e) => new re(e, { used: A(), positionx: E(), positiony: E(), positionz: E(), prevPositionx: E(), prevPositiony: E(), prevPositionz: E(), forcex: a(), forcey: a(), forcez: a(), quaternionx: a(), quaterniony: a(), quaternionz: a(), quaternionw: a(), prevQuaternionx: a(), prevQuaterniony: a(), prevQuaternionz: a(), prevQuaternionw: a(), localVelocityx: a(), localVelocityy: a(), localVelocityz: a(), externalVelocityx: a(), externalVelocityy: a(), externalVelocityz: a(), attractorx: a(), attractory: a(), attractorz: a(), orbitOriginx: a(), orbitOriginy: a(), orbitOriginz: a(), deltaPosLocalBetweenRafx: a(), deltaPosLocalBetweenRafy: a(), deltaPosLocalBetweenRafz: a(), friction: a(), xAxisCtrl: a(), xAxisMouseCtrl: a(), yAxisCtrl: a(), turnCapacity: a(), forceCapacity: a(), mass: a(), slideFrom: a(), slideFromSides: a(), slideBonus: a(), colliding: A(), sliding: A(), surfing: A(), flying: A(), falling: A(), ground: A(), sleep: A(), move: A(), moving: A(), jumping: A(), drifting: A(), driftingDir: A(), driftingStart: E(), driftPressed: A(), wishJump: A(), orbital: A(), magneticFeet: A(), driving: A(), canJump: A(), canFly: A(), hitWall: A(), autoWalk: A(), maxVelocity: Me(), normalRaycastx: a(), normalRaycasty: a(), normalRaycastz: a(), followNormal: A(), partialSleep: A() }), it = (e) => new re(e, { used: A(), positionx: a(), positiony: a(), positionz: a(), prevPositionx: a(), prevPositiony: a(), prevPositionz: a(), quaternionx: a(), quaterniony: a(), quaternionz: a(), quaternionw: a(), prevQuaternionx: a(), prevQuaterniony: a(), prevQuaternionz: a(), prevQuaternionw: a(), friction: a() }), at = (e) => new re(e, { used: A(), positionx: a(), positiony: a(), positionz: a() }), ot = (e) => new ze(e, { used: A(), positionx: E(), positiony: E(), positionz: E(), prevPositionx: E(), prevPositiony: E(), prevPositionz: E(), quaternionx: a(), quaterniony: a(), quaternionz: a(), quaternionw: a(), prevQuaternionx: a(), prevQuaterniony: a(), prevQuaternionz: a(), prevQuaternionw: a(), resetCamera: A(), resetCameraNoLerp: A(), dragDamp: a(), dragDeltax: a(), dragDeltay: a(), targetOffsetx: a(), targetOffsety: a(), targetOffsetz: a(), cameraOffsetx: a(), cameraOffsety: a(), cameraOffsetz: a(), fixedCameraOffsetx: a(), fixedCameraOffsety: a(), fixedCameraOffsetz: a(), baseCameraRotationx: a(), baseCameraRotationy: a(), preventDrag: A(), disableCameraPositionLerp: A(), offsetAdjuster: a(), absoluteDeltaNoLerpx: a(), absoluteDeltaNoLerpy: a(), absoluteDeltax: a(), absoluteDeltay: a(), targetHeight: a(), eyesHeight: a(), invertX: A(), invertY: A(), autoTilt: A(), gentleVR: A(), lockRotation: A(), firstPerson: A(), angleOffset: a(), prevAngleOffset: a(), forcequaternionTotalY: A(), nextQuaternionTotalYx: a(), nextQuaternionTotalYy: a(), nextQuaternionTotalYz: a(), nextQuaternionTotalYw: a(), transformerYTotalRot: a(), turnVRCam: A(), followNormal: A(), dialogMode: A(), dialogTargetx: a(), dialogTargety: a(), dialogTargetz: a(), pctDistFromPlayer: a(), sideViewFrom: a(), dialogUseOffset: A(), targetDist: a(), aspect: a(), disableCollider: A(), ditheringMode: A(), birdView: A(), aimMode: A() }), ct = (e) => new re(e, { used: A(), inZone: A() }), st = (e) => new re(e, { used: A(), affectCamera: A(), friction: a() }), lt = (e) => {
  const t = new Float32Array(e);
  return t._itemLength = 4, t;
}, dt = (e) => new ze(e, { slideYforce: a(), lineardrag: a(), gravityScalar: a(), steps: a(), fps: A(), paused: A(), pausedAtStart: A(), countFrom: E(), pageVisible: A(), physicCalculatingFrame: A(), physicIsWriting: A(), physicReadingTempArray: A(), frontWritingTempArray: A(), frontIsReading: A(), physicBeforeLastFrameTime: E(), physicLastFrameTime: E(), serverOffset: E(), slideEnabled: A(), camRaycasterOriginx: a(), camRaycasterOriginy: a(), camRaycasterOriginz: a(), camRaycasterDirectionx: a(), camRaycasterDirectiony: a(), camRaycasterDirectionz: a(), camRaycasterFar: a(), camRaycasterHit: A(), gravityRaycasterOriginx: a(), gravityRaycasterOriginy: a(), gravityRaycasterOriginz: a(), gravityRaycasterDirectionx: a(), gravityRaycasterDirectiony: a(), gravityRaycasterDirectionz: a(), gravityRaycasterFar: a(), gravityRaycasterHit: A(), targetPosx: a(), targetPosy: a(), targetPosz: a(), gravityUpx: a(), gravityUpy: a(), gravityUpz: a(), gravityDirectionDamp: a(), camTrackPosx: a(), camTrackPosy: a(), camTrackPosz: a(), camSubTrackPosx: a(), camSubTrackPosy: a(), camSubTrackPosz: a(), cameraObjXpositionx: a(), cameraObjXpositiony: a(), cameraObjXpositionz: a(), cameraObjXquatx: a(), cameraObjXquaty: a(), cameraObjXquatz: a(), cameraObjXquatw: a(), cameraObjYpositionx: a(), cameraObjYpositiony: a(), cameraObjYpositionz: a(), cameraObjYquatx: a(), cameraObjYquaty: a(), cameraObjYquatz: a(), cameraObjYquatw: a(), currentStaticsInArea: Me(), cameraDampingFactor: a(), cameraTargetDampingFactor: a(), groundFriction: a(), airFriction: a(), driftBonus: A(), debug: A() }), ut = (e) => {
  const t = new Float32Array(e);
  return t._itemLength = 4, t;
}, Ve = 2.1875, Ne = 1, Ae = 0.5, ve = 0.5, Pe = /* @__PURE__ */ Se((e, t) => {
  const r = -(Ae + ve / 2);
  return { debugBvh: false, debugPhysic: false, debugInfos: false, sharedDynamic: null, sharedKinematic: null, tempSharedDynamic: null, sharedStatic: null, sharedCamera: null, sharedPeerKinematic: null, sharedZone: null, sharedInReach: null, sharedPhysic: null, useSharedArray: false, physicStarted: false, physicIsInit: false, totalEssentialReady: 0, readPhysicStatePriority: 2, jumpVelocity: 12, forceCapacity: Ne, turnCapacity: Ve, playerApi: {}, kinematicApis: {}, capsuleRadius: Ae, capsuleLength: ve, halfCapsule: r, incTotalEssentialReady: (i) => {
    e({ totalEssentialReady: t().totalEssentialReady + i });
  } };
}), _ = (e) => Pe(e, Fe);
Object.assign(_, Pe);
const { getState: z, setState: ne } = Pe, ft = 0.4, yt = 1.6, mt = new B.Euler(), Z = new B.Quaternion(), q = { FIRST_PERSON: "firstPerson", THIRD_PERSON: "thirdPerson", BIRD_VIEW: "birdView", VR: "vr" }, we = /* @__PURE__ */ Se((e, t) => ({ isDragging: false, playingAnimation: false, dragSensitivity: 1, renderPriority: 5, preventReset: false, defaultFov: 50, targetOffset: new B.Vector3(0, 1.75, 0.5), cameraOffset: new B.Vector3(0, 3.25, -10.25), setTargetOffset: (r) => {
  if (isNaN(r[0]) || isNaN(r[1]) || isNaN(r[2])) {
    console.error("Invalid value:", r);
    debugger;
  } else
    t().targetOffset.set(r[0], r[1], r[2]);
}, setCameraOffset: (r) => {
  if (isNaN(r[0]) || isNaN(r[1]) || isNaN(r[2])) {
    console.error("Invalid value:", r);
    debugger;
  } else
    t().cameraOffset.set(r[0], r[1], r[2]);
}, fixedCameraOffset: new B.Vector3(0, 0, 0), defaultTargetOffset: new B.Vector3(0, 1.75, 0.5), cameraOffsetDefault: new B.Vector3(0, 3.25, -10.25), cameraOffsetBirdView: new B.Vector3(15, 15, -15), disableThirdPersonFree: true, aimMode: false, disableBirdView: false, lockRotation: false, showHelpers: false, refCamera: null, noIdleZone: false, cinematicMode: false, dialogMode: false, dialogTarget: new B.Vector3(), cinematicDamp: 1, cinematicPositionTo: new B.Vector3(), cinematicQuaternionTo: new B.Quaternion(), editor: false, cinematicFov: 50, interruptCinematic: false, orbitCtrlReady: false, dom: Ie(null), control: Ie(null), orbitCtrl: false, vrSession: false, firstPerson: false, birdView: false, gentleVR: false, setDialogMode: (r, i = {}) => {
  const { sharedCamera: o } = /* @__PURE__ */ z();
  e({ dialogMode: r }), o.dialogMode = r, Object.assign(o, i);
}, switchCamera: (r, i, o, c) => {
  const u = /* @__PURE__ */ [q.FIRST_PERSON, q.VR].includes(r), s = r === q.BIRD_VIEW;
  e({ lockRotation: o, firstPerson: u, birdView: s, aimMode: c }), k("changeCameraMode", { mode: r }), k("resetOffsetCamera"), k("hideModel", { hide: i });
  const { sharedCamera: l } = /* @__PURE__ */ z();
  l.resetCamera = true, l.firstPerson = u, l.birdView = s, l.gentleVR = r === q.VR, l.lockRotation = o, l.ditheringMode = s, l.aimMode = c, e({ cameraHasBeenSet: true });
}, resetCameraRotationFromDynamic: () => {
  const { sharedCamera: r, sharedDynamic: i } = /* @__PURE__ */ z();
  Z.set(i[0].quaternionx, i[0].quaterniony, i[0].quaternionz, i[0].quaternionw);
  const o = /* @__PURE__ */ mt.setFromQuaternion(Z);
  o.reorder("YXZ"), r.transformerYTotalRot = o.y, r.absoluteDeltax = B.MathUtils.radToDeg(o.y), r.absoluteDeltaNoLerpx = B.MathUtils.radToDeg(o.y), r.angleOffset = 0, r.prevAngleOffset = 0, Z.identity(), r.nextQuaternionTotalYx = Z.x, r.nextQuaternionTotalYy = Z.y, r.nextQuaternionTotalYz = Z.z, r.nextQuaternionTotalYw = Z.w, r.forcequaternionTotalY = true;
}, setThirdPersonLocked: () => {
  t().switchCamera(q.THIRD_PERSON, false, true, false);
}, setBirdView: () => {
  const { disableBirdView: r, setThirdPersonLocked: i } = /* @__PURE__ */ t();
  if (r) {
    i();
    return;
  }
  t().switchCamera(q.BIRD_VIEW, false, false, false), t().resetCameraRotationFromDynamic();
}, setThirdPersonFree: () => {
  const { disableThirdPersonFree: r, setFirstPerson: i } = /* @__PURE__ */ t();
  if (r) {
    i();
    return;
  }
  t().switchCamera(q.THIRD_PERSON, false, true, false), t().resetCameraRotationFromDynamic();
}, setFirstPerson: () => {
  t().switchCamera(q.FIRST_PERSON, true, true, false);
}, setThirdPersonAim: () => {
  t().switchCamera(q.THIRD_PERSON, false, true, true);
}, setGentleVR: () => {
  t().switchCamera(q.VR, true, true, false);
}, setNextCameraMode: () => {
  const { lockRotation: r, firstPerson: i, birdView: o, gentleVR: c } = z().sharedCamera;
  r && !i && !o ? t().setThirdPersonFree() : !r && !i && !o ? t().setFirstPerson() : i ? t().vrSession && !c ? t().setGentleVR() : t().setBirdView() : o && t().setThirdPersonLocked();
} })), le = (e) => we(e, Fe);
Object.assign(le, we);
const { getState: V, setState: H } = we, K = /* @__PURE__ */ We({});
B.BufferGeometry.prototype.computeBoundsTree = He, B.BufferGeometry.prototype.disposeBoundsTree = Ge, B.Mesh.prototype.raycast = Xe;
const qe = "👹 ", I = { gravityScalar: -9.806, gravity: { x: 0, y: -9.806, z: 0 }, lineardrag: 2, steps: 5, fps: 0, cameraDampingFactor: 3, cameraTargetDampingFactor: 10, paused: true, slideEnabled: true, pageVisible: true, groundFriction: 0.5, airFriction: 0.1, driftBonus: 60, interpolationFactor: 1, gravityDirectionDamp: 20, pauseReadPhysic: false, slideYforce: -4, physicEvent: {}, runOnceAfterOnePhysicFrame: (e) => {
  const { workerApiRef: t } = /* @__PURE__ */ z();
  t.current.runOnceAfterOnePhysicFrame(xe.proxy(e));
}, attchProxyEvents: () => {
} };
function xt({ children: e, autoPauseOnPageInactive: t = true, debugMaterial: r = new B.MeshBasicMaterial({ wireframe: true, color: "red" }) }) {
  const i = /* @__PURE__ */ F({ static: [], dynamic: [], zone: [], kinematic: [], peerKinematic: [] }), o = /* @__PURE__ */ F(), c = /* @__PURE__ */ F();
  $e(() => {
    o.current = new Worker(new URL("./assets/oni.worker-5505fa9f.js", import.meta.url), { type: "module" }), self._workerOni = o.current;
    const l = window.crossOriginIsolated;
    c.current = xe.wrap(o.current);
    const h = async () => {
      if (l)
        window.workerApiRef = c, await c.current.init(), console.log(`${qe}Using SharedArrayBuffer! 😊`);
      else {
        let y = function(d) {
        };
        console.log(`${qe}Using ArrayBuffer`), await c.current.init(xe.proxy(y));
      }
      ne({ physicIsInit: true });
    };
    (async () => {
      const y = l ? new SharedArrayBuffer(512) : new ArrayBuffer(512), d = l ? new SharedArrayBuffer(512) : new ArrayBuffer(512), m = l ? new SharedArrayBuffer(16384) : new ArrayBuffer(16384), n = l ? new SharedArrayBuffer(2048) : new ArrayBuffer(2048), x = l ? new SharedArrayBuffer(2e3 * 4 * Float32Array.BYTES_PER_ELEMENT) : new ArrayBuffer(2e3 * 4 * Float32Array.BYTES_PER_ELEMENT), v = l ? new SharedArrayBuffer(512) : new ArrayBuffer(512), P = l ? new SharedArrayBuffer(65536) : new ArrayBuffer(65536), p = l ? new SharedArrayBuffer(2048) : new ArrayBuffer(2048), w = l ? new SharedArrayBuffer(2048) : new ArrayBuffer(2048), b = l ? new SharedArrayBuffer(6 * Float32Array.BYTES_PER_ELEMENT) : new ArrayBuffer(6 * Float32Array.BYTES_PER_ELEMENT), C = /* @__PURE__ */ dt(v), R = /* @__PURE__ */ ot(w);
      ne({ sharedDynamic: _e(y), tempSharedDynamic: _e(d), sharedStatic: st(m), sharedZone: ct(n), sharedInReach: lt(x), sharedKinematic: it(P), sharedPeerKinematic: at(p), sharedCamera: R, sharedPhysic: C, useSharedArray: l, sharedPointer: ut(b) }), await c.current.setSharedArrayBuffer(y, d, m, n, x, v, P, p, w, b), ne({ sharedArrayReady: true }), C.lineardrag = 4, C.gravityScalar = -23, C.slideEnabled = 0, C.gravityDirectionDamp = 1e3, C.slideYforce = -10, R.offsetAdjuster = 1, ((Q) => {
        for (const [S, T] of Object.entries(Q))
          if (C[S] !== void 0)
            if (typeof T == "object")
              for (const [ce, se] of Object.entries(T))
                C[S + ce] = se, l || c.current.syncSAB("sharedPhysic", null, S, ce, se);
            else
              C[S] = T, l || c.current.syncSAB("sharedPhysic", null, S, null, T);
        Object.keys(C).forEach((S) => {
          Object.defineProperty(Q, S, { configurable: true, get: function() {
            return C[S];
          }, set: function(T) {
            if (typeof T == "object")
              for (const [ce, se] of Object.entries(T))
                C[S][ce] = se, l || c.current.syncSAB("sharedPhysic", null, S, ce, se);
            else
              C[S] = T, l || c.current.syncSAB("sharedPhysic", null, S, null, T);
          } });
        });
      })(I), I.sharedPhysic = C, h();
    })();
    function f() {
      c.current.attachEventGate(xe.proxy((y, d) => {
        k("physicEvent", { target: y, params: d });
      }));
    }
    return ne({ attchProxyEvents: f }), f(), () => {
      o.current?.terminate(), self._workerOni = null;
    };
  }, []), $("physicEvent", ({ target: l, params: h }) => {
    if (Array.isArray(l))
      for (let g = 0; g < l.length; g++) {
        const { target: f, params: y } = l[g];
        I.physicEvent[f](y);
      }
    else
      typeof I.physicEvent[l] == "function" && I.physicEvent[l](h);
  });
  const u = /* @__PURE__ */ L(() => ({ workerRef: o, workerApiRef: c, bodies: i, debugMaterial: r }), [o, c, i]);
  O(() => {
    ne({ workerApiRef: c });
  }, [c]);
  const s = /* @__PURE__ */ _((l) => l.readPhysicStatePriority);
  return te(function(h, g, f, y) {
    if (!I.sharedPhysic || I.pauseReadPhysic)
      return;
    const d = performance.timeOrigin + h.clock.startTime + h.clock.elapsedTime * 1e3;
    if (I.sharedPhysic.physicIsWriting) {
      let v = performance.now(), P = true;
      for (; I.sharedPhysic.physicIsWriting && P; )
        P = performance.now() - v < 500;
    }
    I.sharedPhysic.frontIsReading = true;
    const m = I.sharedPhysic.physicLastFrameTime, n = I.sharedPhysic.physicBeforeLastFrameTime;
    let x = 1;
    d - m !== m - n && (x = (d - m) / (m - n)), x === 1 / 0 && (x = 0), I.interpolationFactor = x, I.currentMainFrame = d, k("readPhysicState", { state: h, delta: g, isXR: f }), I.sharedPhysic.frontIsReading = false;
  }, s), Ce(K.Provider, { value: u, children: [W(pt, {}), W(ht, { autoPauseOnPageInactive: t }), W(Ue, { fallback: null, children: e })] });
}
const pt = () => (O(() => {
  function e() {
    const { workerApiRef: t } = /* @__PURE__ */ z();
    t && t.current && t.current.click();
  }
  return document.addEventListener("click", e), () => {
    document.removeEventListener("click", e);
  };
}, []), te(({ pointer: e, camera: t }) => {
  const { sharedPointer: r, sharedCamera: i } = /* @__PURE__ */ z();
  if (!r)
    return;
  r[0] = 0;
  const { aimMode: o, drawMode: c } = /* @__PURE__ */ V();
  o && !c ? (r[1] = 0, r[2] = 0) : (r[1] = e.x, r[2] = e.y), i.aspect = t.aspect;
}), null), ht = ({ autoPauseOnPageInactive: e }) => {
  const t = /* @__PURE__ */ Re();
  return O(() => {
    e && (I.pageVisible = t);
  }, [t, e]), null;
}, gt = { strategy: Je, maxDepth: 40, maxLeafTris: 4, setBoundingBox: true, useSharedArrayBuffer: false, onProgress: null, verbose: true }, pe = new B.Vector3(), G = (e, t, r, i) => {
  let o = null, c = null;
  if (t)
    c = t.geometry.clone();
  else if (e.type === "Group") {
    const u = [];
    if (e.traverse((s) => {
      s.updateMatrix();
    }), e.updateWorldMatrix(true, true), e.traverse((s) => {
      if (s.geometry) {
        const l = /* @__PURE__ */ s.geometry.clone();
        l.applyMatrix4(s.matrixWorld);
        for (const h in l.attributes)
          h !== "position" && l.deleteAttribute(h);
        u.push(l);
      }
    }), u.length === 0)
      return;
    c = rt(u, false);
  } else
    e.type === "Mesh" && (e.updateMatrix(), e.updateMatrixWorld(), i === "kinematic" ? (pe.setFromMatrixScale(e.matrixWorld), c = e.geometry.clone(), c.scale(pe.x, pe.y, pe.z)) : (c = e.geometry.clone(), i !== "peerKinematic" && c.applyMatrix4(e.matrixWorld)));
  return o = new B.Mesh(c, r), i === "static" && (o.matrixWorldAtCreation = new B.Matrix4().copy(e.matrixWorld)), o;
}, ie = (e) => {
  const t = /* @__PURE__ */ me((u) => u.scene), [r, i] = /* @__PURE__ */ _((u) => [u.debugBvh, u.debugPhysic]);
  F(null);
  const o = /* @__PURE__ */ F(null), { debugMaterial: c } = /* @__PURE__ */ Y(K);
  return O(() => {
    if (!i)
      return;
    let u, s;
    if (e.type === "inReach")
      s = new B.Mesh(new B.SphereGeometry(e.radius, 16, 16), c), t.add(s), e.collideBox = s;
    else {
      if (!e.collideBox)
        return;
      e.collideBox.geometry.boundsTree = new et(e.collideBox.geometry, gt), e.collideBox.noRenderInPreview = true, t.add(e.collideBox), u = new tt(e.collideBox), r && (u.displayEdges = true, u.displayParents = true, u.opacity = 1, u.color.setStyle("#00ffff"), t.add(u)), o.current && (!e.collideBox || o.current.uuid !== e.collideBox.uuid) && (o.current.removeFromParent(), u.removeFromParent()), o.current = e.collideBox;
    }
    return () => {
      if (u && u.removeFromParent(), e.collideBox && e.collideBox.removeFromParent(), delete e.collideBox, e.type === "inReach" && s) {
        s.removeFromParent();
        return;
      }
    };
  }), Bt(e), null;
}, Bt = (e) => {
  const t = /* @__PURE__ */ _((r) => r.debugBvh);
  te(() => {
    t && (!e.collideBox || e.collideBox && ["static", "zone"].includes(e.type) || (e.type === "inReach" ? e.collideBox.position.set(e.positionx, e.positiony, e.positionz) : (e.collideBox.position.set(e.positionx, e.positiony, e.positionz), e.collideBox.quaternion.set(e.quaternionx, e.quaterniony, e.quaternionz, e.quaternionw)), e.collideBox.updateMatrix(), e.collideBox.updateWorldMatrix(false, false), e.collideBox.jumpBox && (e.collideBox.jumpBox.matrix.copy(e.collideBox.matrix), e.collideBox.jumpBox.matrixWorld.copy(e.collideBox.matrixWorld)), e.collideBox.wallBox && (e.collideBox.wallBox.matrix.copy(e.collideBox.matrix), e.collideBox.wallBox.matrixWorld.copy(e.collideBox.matrixWorld))));
  }, 21);
}, At = (e, t = { moving: false }) => {
  const [r, i, o] = /* @__PURE__ */ _((g) => [g.sharedInReach, g.sharedArrayReady, g.useSharedArray]), c = /* @__PURE__ */ F(false), { workerApiRef: u } = /* @__PURE__ */ Y(K), s = /* @__PURE__ */ L(() => {
    let g = e ? e.current : null;
    t.radius || console.warn("useInReach requires radius ");
    const f = { type: "inReach", entity: g, zoneIds: [0], sleep: false, moving: false, toggleSleep: (y) => {
      f.sleep = y, !(f.indexInArray === null || isNaN(f.indexInArray)) && u.current.toggleBodyProp("inReach", f.indexInArray, "sleep", y);
    }, ...t };
    return f;
  }, [e]);
  O(() => {
    s.toggleSleep(t.sleep);
  }, [t.sleep]), O(() => {
    s.radius = t.radius, !(s.indexInArray === null || isNaN(s.indexInArray)) && u.current.toggleBodyProp("inReach", s.indexInArray, "radius", s.radius);
  }, [t.radius]);
  const l = () => {
    s.indexInArray === null || isNaN(s.indexInArray) || !t.onInReachChange || (I.physicEvent[`inReach_${s.indexInArray}`] = s.onInReachChange, u.current.attachInReachCB(s.indexInArray));
  }, h = () => {
    s.indexInArray === null || isNaN(s.indexInArray) || (u.current.detachInReachCB(s.indexInArray), delete I.physicEvent[`inReach_${s.indexInArray}`]);
  };
  return O(() => (s.onInReachChange = t.onInReachChange, l(), () => {
    h();
  }), [s.indexInArray, t.onInReachChange]), O(() => {
    let g = e && e.hasOwnProperty("current") ? e.current : e;
    if (!i)
      return;
    const f = async () => {
      const y = g && g.position && g.position.toArray() || [s.positionx, s.positiony, s.positionz], d = await u.current.registerInReach(y, { moving: s.moving, radius: s.radius, radiusSquared: s.radius * s.radius, sleep: true });
      return s.indexInArray = d, l(), Object.defineProperty(s, "positionx", { get: function() {
        return r[d * r._itemLength + 1];
      }, set: function(m) {
        r[d * r._itemLength + 1] = m;
      }, configurable: true }), Object.defineProperty(s, "positiony", { get: function() {
        return r[d * r._itemLength + 2];
      }, set: function(m) {
        r[d * r._itemLength + 2] = m;
      }, configurable: true }), Object.defineProperty(s, "positionz", { get: function() {
        return r[d * r._itemLength + 3];
      }, set: function(m) {
        r[d * r._itemLength + 3] = m;
      }, configurable: true }), s.toggleSleep(s.sleep), d;
    };
    return c.current = true, f().then((y) => {
      c.current || (u.current.unregisterInReach(y), h(), delete s.indexInArray);
    }), () => {
      c.current = false, s.indexInArray !== void 0 && (u.current.unregisterInReach(s.indexInArray), delete s.indexInArray);
    };
  }, [e, r, i]), ie(s), [s];
}, vt = (e, t) => {
  e.geometry.computeBoundingBox();
  const r = /* @__PURE__ */ e.geometry.boundingBox.clone();
  r.translate(new B.Vector3(0, -1, 0));
  const i = /* @__PURE__ */ new B.Vector3().subVectors(r.max, r.min), o = new B.BoxGeometry(i.x, i.y, i.z);
  o.scale(0.5, 1, 0.5);
  const c = /* @__PURE__ */ new B.Matrix4().setPosition(i.addVectors(r.min, r.max).multiplyScalar(0.5));
  o.applyMatrix4(c), e.jumpBox = new B.Mesh(o, t), e.add(e.jumpBox), e.jumpBox.visible = true;
}, Pt = (e, t) => {
  e.geometry.computeBoundingBox();
  const r = /* @__PURE__ */ e.geometry.boundingBox.clone();
  r.translate(new B.Vector3(0, -4.5, 3));
  const i = /* @__PURE__ */ new B.Vector3().subVectors(r.max, r.min), o = new B.ConeGeometry(2.5, 10, 10), c = /* @__PURE__ */ new B.Matrix4().setPosition(i.addVectors(r.min, r.max).multiplyScalar(0.5));
  o.applyMatrix4(c), o.rotateX(-Math.PI / 2), o.scale(1, 0.25, 0.35), e.wallBox = new B.Mesh(o, t), e.add(e.wallBox), e.wallBox.visible = true;
}, de = (e, t, r) => {
  Object.keys(e).forEach((i) => {
    t[i] !== void 0 && (t[i] = e[i]);
  }), Object.keys(t).forEach((i) => {
    Object.defineProperty(e, i, { get: function() {
      return r[i];
    }, set: function(o) {
      t[i] = o;
    }, configurable: true });
  });
}, wt = (e, t = { entity: null, friction: 1, mass: 1, affectCamera: true, sleep: false, zoneIds: [0], zones: {}, hitBoxRef: null, geoType: "capsule", forceCapacity: Ne, turnCapacity: Ve }, r) => {
  const [i, o, c, u, s] = /* @__PURE__ */ _((y) => [y.sharedDynamic, y.sharedArrayReady, y.tempSharedDynamic, y.useSharedArray, y.debugPhysic]), { workerApiRef: l, bodies: h, debugMaterial: g } = /* @__PURE__ */ Y(K), f = /* @__PURE__ */ L(() => {
    let y = e && e.hasOwnProperty("current") ? e.current : e;
    const d = { type: "dynamic", friction: 1, mass: 1, zones: {}, affectCamera: true, entity: y, zoneIds: [0], collideBox: null, ignorePeer: false, controlsLocked: false, toggleBodyProp: (m, n) => {
      d[m] = n, !(d.indexInArray === null || isNaN(d.indexInArray)) && l.current.toggleBodyProp("dynamic", d.indexInArray, m, n);
    }, ...t };
    return d.positionx = 0, d.positiony = 0, d.positionz = 0, d.quaternionx = 0, d.quaterniony = 0, d.quaternionz = 0, d.quaternionw = 1, d;
  }, [e, t.hitBoxRef]);
  return O(() => {
    let y = e && e.hasOwnProperty("current") ? e.current : e, d = null;
    const { hitBoxRef: m } = f;
    if (!m.current)
      return;
    const n = m.current, x = h.current, v = l.current, P = "dynamic";
    if (!y || !o)
      return;
    const p = /* @__PURE__ */ G(y, n, g, P);
    return vt(p, g), Pt(p, g), y.quaternion.length() > 0 && p.quaternion.copy(y.quaternion), p.type = P, p.api = f, (async () => {
      d = p.geometry;
      const b = { position: d.attributes.position.array, index: d.index.array, geoType: t.geoType ? t.geoType : "capsule" };
      d.attributes.normal && (b.normal = d.attributes.normal.array);
      const C = p.jumpBox.geometry;
      let R = { position: C.attributes.position.array, index: C.index.array };
      const N = p.wallBox.geometry;
      let Q = { position: N.attributes.position.array, index: N.index.array };
      x[P].push(p), f.collideBox = p, console.log("[@oni] add dynamic body ", e, i, o);
      const S = await v.addBody(b, P, { friction: f.friction, mass: f.mass }, R, Q, { ignorePeer: f.ignorePeer, controlsLocked: f.controlsLocked });
      de(f, c[S], i[S]), f.indexInArray = S, f.entity = y, f.toggleBodyProp("sDynamicSet", true), f.toggleBodyProp("roles", t.roles), r && r(f, v);
    })(), () => {
      if (y) {
        if (console.log("[@oni] remove dynamic body ", y, f.indexInArray), f.indexInArray === void 0) {
          console.error("[@oni] could not remove dynamic body ", y, f.indexInArray);
          return;
        }
        v.removeBody(P, f.indexInArray), p.wallBox && p.remove(p.wallBox), p.jumpBox && p.remove(p.jumpBox), delete f.indexInArray, s && f.collideBox && f.collideBox.removeFromParent(), delete x[P][f.indexInArray], delete f.collideBox;
      }
    };
  }, [e, t.hitBoxRef, i, o]), O(() => {
    f.indexInArray !== void 0 && f.toggleBodyProp("roles", t.roles);
  }, [f.indexInArray, t.roles, t.roles?.length]), ie(f), f;
}, he = function(e) {
  return JSON.stringify(e, function(t, r) {
    var i;
    return r instanceof Function || typeof r == "function" ? (i = r.toString(), i.length < 8 || i.substring(0, 8) !== "function" ? "_NuFrRa_" + i : i) : r instanceof RegExp ? "_PxEgEr_" + r : r;
  });
};
function ge(e) {
  const t = e.data.array.constructor, r = e.count, i = e.itemSize, o = e.normalized, c = new t(r * i);
  let u;
  e.isInstancedInterleavedBufferAttribute ? u = new B.InstancedBufferAttribute(c, i, o, e.meshPerAttribute) : u = new B.BufferAttribute(c, i, o);
  for (let s = 0; s < r; s++)
    u.setX(s, e.getX(s)), i >= 2 && u.setY(s, e.getY(s)), i >= 3 && u.setZ(s, e.getZ(s)), i >= 4 && u.setW(s, e.getW(s));
  return u;
}
const Be = async (e, t, r, i, o, c, u, s) => {
  const l = r.geometry, h = { index: l.index.array };
  u !== void 0 && (h.side = u), l.attributes.position.isInterleavedBufferAttribute ? h.position = ge(l.attributes.position).array : h.position = l.attributes.position.array, l.attributes.faceGeoId && (h.faceGeoId = l.attributes.faceGeoId.array), s && s(h), l.attributes.normal && (l.attributes.normal.isInterleavedBufferAttribute ? h.normal = ge(l.attributes.normal).array : h.normal = l.attributes.normal.array), i.collideBox = r;
  const g = await e.addBody(h, t, o, void 0, void 0, c);
  return i.indexInArray = g, g;
}, De = (e, t) => {
  e.positionx = t.position.x, e.positiony = t.position.y, e.positionz = t.position.z, e.quaternionx = t.quaternion.x, e.quaterniony = t.quaternion.y, e.quaternionz = t.quaternion.z, e.quaternionw = t.quaternion.w, e.scalex = t.scale.x, e.scaley = t.scale.y, e.scalez = t.scale.z, e.entity = t;
}, je = async (e, t, r, i, o) => {
  const c = r.geometry, u = { index: c.index.array };
  return c.attributes.position.isInterleavedBufferAttribute ? u.position = ge(c.attributes.position).array : u.position = c.attributes.position.array, c.attributes.normal && (c.attributes.normal.isInterleavedBufferAttribute ? u.normal = ge(c.attributes.normal).array : u.normal = c.attributes.normal.array), await e.updateBody(u, t, i, o), true;
}, bt = (e, t = { entity: null, friction: 1, affectCamera: true, sleep: false, zoneIds: [0], zones: {}, essential: false, refreshCollide: false, timeOffset: 0 }) => {
  const [r, i, o, c, u] = /* @__PURE__ */ _((p) => [p.sharedKinematic, p.sharedArrayReady, p.useSharedArray, p.incTotalEssentialReady, p.debugPhysic]), [s, l] = /* @__PURE__ */ ee(false), h = /* @__PURE__ */ F(false), g = /* @__PURE__ */ F(false), f = /* @__PURE__ */ F(false), { workerApiRef: y, bodies: d, debugMaterial: m } = /* @__PURE__ */ Y(K), n = /* @__PURE__ */ L(() => {
    let p = e && e.hasOwnProperty("current") ? e.current : e, w = {};
    if (t.id) {
      const { kinematicApis: b } = /* @__PURE__ */ z();
      b[t.id] && (w = b[t.id]);
    }
    return Object.assign(w, { friction: 1, zones: {}, affectCamera: true, entity: p, rotationAxis: [0, 1, 0], pFrom: [0, 0, 0], pTo: [0, 0, 0], rFrom: [0, 0, 0, 1], rTo: [0, 0, 0, 1], zoneIds: [0], collideBox: null, timeOffset: 0, sleepCollide: false, setTimeOffset: (b) => {
      w.timeOffset = b, y.current.toggleBodyProp("kinematic", w.indexInArray, "timeOffset", b);
    }, toggleSleep: (b) => {
      w.sleep = b, y.current.toggleBodyProp("kinematic", w.indexInArray, "sleep", b);
    }, togglePhysicBodyProp: (b, C) => {
      w[b] = C, y.current.toggleBodyProp("kinematic", w.indexInArray, b, C);
    }, ...t }), w;
  }, [e]);
  O(() => {
    let p = t.id;
    const { kinematicApis: w } = /* @__PURE__ */ z();
    return w[t.id] = n, () => {
      delete w[p];
    };
  }, [t.id, n]), O(() => {
    n.togglePhysicBodyProp("sleep", t.sleep), n.togglePhysicBodyProp("sleepCollide", t.sleepCollide), n.togglePhysicBodyProp("forcedTime", t.forcedTime), t.timeOffset !== void 0 && n.togglePhysicBodyProp("timeOffset", t.timeOffset), !(n.indexInArray === null || isNaN(n.indexInArray)) && y.current.updateKinematicAndChildsOnce(n.indexInArray).then(() => {
      P(n, y.current);
    });
  }, [t.sleep, t.sleepCollide, t.forcedTime, t.timeOffset]), O(() => {
    n.managerFunc = t.managerFunc, !(n.indexInArray === null || isNaN(n.indexInArray)) && y.current.addKinematicManager(n.indexInArray, n.managerFunc ? he({ managerFunc: n.managerFunc }) : null);
  }, [t.managerFunc]), O(() => {
    n.collideManager = t.collideManager, !(n.indexInArray === null || isNaN(n.indexInArray)) && y.current.addKinematicCollideManager(n.indexInArray, n.collideManager ? he({ collideManager: n.collideManager }) : null);
  }, [t.collideManager]), O(() => {
    n.friction = t.friction, n.rotationAxis = t.rotationAxis, n.pFrom = t.pFrom, n.pTo = t.pTo, n.rFrom = t.rFrom, n.rTo = t.rTo;
  }, [t.friction, t.rotationAxis, t.pFrom, t.pTo, t.rFrom, t.rTo]);
  const x = () => {
    n.indexInArray === null || isNaN(n.indexInArray) || (t.onCollide && (I.physicEvent[`kinematic_collide_${n.indexInArray}`] = n.onCollide, y.current.attachCollideCB("kinematic", n.indexInArray)), t.onStopCollide && (I.physicEvent[`kinematic_stop_collide_${n.indexInArray}`] = n.onStopCollide, y.current.attachStopCollideCB("kinematic", n.indexInArray)));
  }, v = () => {
    y.current.detachCollideCB("kinematic", n.indexInArray), delete I.physicEvent[`kinematic_collide_${n.indexInArray}`], y.current.detachStopCollideCB("kinematic", n.indexInArray), delete I.physicEvent[`kinematic_stop_collide_${n.indexInArray}`];
  }, P = (p, w) => {
    t.onReady && t.onReady(p, w), k("updateInitKinematics", p.indexInArray), p.essential && c(1);
  };
  return O(() => (n.onCollide = t.onCollide, n.onStopCollide = t.onStopCollide, x(), () => {
    v();
  }), [n.indexInArray, t.onCollide, t.onStopCollide]), O(() => {
    let p = e && e.hasOwnProperty("current") ? e.current : e;
    const w = d.current, b = y.current, C = "kinematic";
    if (!p || !i)
      return;
    if (h.current) {
      g.current = true;
      return;
    }
    const R = /* @__PURE__ */ G(p, null, m, C);
    return R.type = C, R.api = n, De(n, p), f.current = true, h.current = true, Be(b, C, R, n, { friction: n.friction, rotationAxis: n.rotationAxis, pFrom: n.pFrom, pTo: n.pTo, rFrom: n.rFrom, rTo: n.rTo }, { sleep: n.sleep, timeOffset: n.timeOffset, forcedTime: n.forcedTime, sleepCollide: n.sleepCollide }).then((N) => {
      if (h.current = false, !f.current || g.current) {
        b.removeBody(C, N), g.current && (l(!s), g.current = false), v(), delete n.indexInArray, u && R.removeFromParent(), delete n.collideBox, delete w[C][N];
        return;
      } else {
        if (w[C][N] = R, b.addKinematicManager(N, n.managerFunc ? he({ managerFunc: n.managerFunc }) : null), b.addKinematicCollideManager(N, n.collideManager ? he({ collideManager: n.collideManager }) : null), x(), de(n, r[N], r[N]), t.child && t.child.length > 0) {
          const { kinematicApis: Q } = /* @__PURE__ */ z();
          for (let S = 0; S < t.child.length; S++) {
            let T = Q[t.child[S]];
            T && T.indexInArray !== null && !isNaN(T.indexInArray) ? y.current.makeParent(n.indexInArray, T.indexInArray).then(() => {
              P(n, b);
            }) : T || (T = Q[t.child[S]] = {}), T.parent = t.id;
          }
        }
        if (n.parent) {
          const { kinematicApis: Q } = /* @__PURE__ */ z(), S = Q[n.parent];
          S && S.indexInArray !== null && !isNaN(S.indexInArray) && y.current.makeParent(S.indexInArray, n.indexInArray).then(() => {
            P(n, b);
          }), delete n.needMakeChild;
        }
        (!t.child || t.child.length === 0) && !n.parent && y.current.addToTree(n.indexInArray).then(() => {
          P(n, b);
        });
      }
    }), () => {
      f.current = false, n.indexInArray !== void 0 && (b.removeBody(C, n.indexInArray), delete n.indexInArray, u && n.collideBox && n.collideBox.removeFromParent(), delete w[C][n.indexInArray], delete n.collideBox);
    };
  }, [e, i, r, s]), O(() => {
    let p = e && e.hasOwnProperty("current") ? e.current : e;
    if (!p || !i || h.current)
      return;
    const w = "kinematic", b = d.current, C = y.current, R = /* @__PURE__ */ G(p, null, m, w);
    return R.type = w, R.api = n, n.collideBox = R, je(C, w, n.collideBox, n.indexInArray), b[w][n.indexInArray] = R, () => {
      u && n.collideBox && n.collideBox.removeFromParent();
    };
  }, [t.refreshCollide]), O(() => {
    n.indexInArray !== void 0 && (y.current.toggleKinematicBodyAttr(n.indexInArray, { pFrom: n.pFrom, pTo: n.pTo, rFrom: n.rFrom, rTo: n.rTo, rotationAxis: n.rotationAxis }), y.current.updateKinematicAndChildsOnce(n.indexInArray).then(() => {
      P(n, y.current);
    }));
  }, [t.rFrom, t.rTo, t.pFrom, t.pTo, t.rotationAxis]), ie(n), n;
}, It = (e, t = { entity: null, sleep: false }) => {
  const [r, i, o, c] = /* @__PURE__ */ _((v) => [v.sharedPeerKinematic, v.sharedArrayReady, v.useSharedArray, v.debugPhysic]), [u, s] = /* @__PURE__ */ ee(false), l = /* @__PURE__ */ F(false), h = /* @__PURE__ */ F(false), g = /* @__PURE__ */ F(false), { workerApiRef: f, bodies: y, debugMaterial: d } = /* @__PURE__ */ Y(K), m = /* @__PURE__ */ L(() => {
    const P = { entity: e && e.hasOwnProperty("current") ? e.current : e, collideBox: null, toggleSleep: (p) => {
      P.sleep = p, !(P.indexInArray === null || isNaN(P.indexInArray)) && f.current.toggleBodyProp("peerKinematic", P.indexInArray, "sleep", p);
    }, ...t };
    return P;
  }, [e]);
  O(() => {
    m.toggleSleep(t.sleep);
  }, [t.sleep]);
  const n = () => {
    m.indexInArray === null || isNaN(m.indexInArray) || (t.onCollide && (I.physicEvent[`peerKinematic_collide_${m.indexInArray}`] = m.onCollide, f.current.attachCollideCB("peerKinematic", m.indexInArray)), t.onStopCollide && (I.physicEvent[`peerKinematic_stop_collide_${m.indexInArray}`] = m.onStopCollide, f.current.attachStopCollideCB("peerKinematic", m.indexInArray)));
  }, x = () => {
    m.indexInArray === null || isNaN(m.indexInArray) || (f.current.detachCollideCB("peerKinematic", m.indexInArray), delete I.physicEvent[`peerKinematic_collide_${m.indexInArray}`], f.current.detachStopCollideCB("peerKinematic", m.indexInArray), delete I.physicEvent[`peerKinematic_stop_collide_${m.indexInArray}`]);
  };
  return O(() => (m.onCollide = t.onCollide, m.onStopCollide = t.onStopCollide, n(), () => {
    x();
  }), [m.indexInArray, t.onCollide, t.onStopCollide]), O(() => {
    let v = e && e.hasOwnProperty("current") ? e.current : e;
    const P = y.current, p = f.current, w = "peerKinematic";
    if (!v || !i)
      return;
    if (l.current) {
      h.current = true;
      return;
    }
    const b = /* @__PURE__ */ G(v, null, d, w);
    return v.quaternion.length() > 0 && b.quaternion.copy(v.quaternion), b.type = w, b.api = m, De(m, v), g.current = true, l.current = true, Be(p, w, b, m, void 0, { sleep: m.sleep }).then((C) => {
      if (l.current = false, !g.current || h.current) {
        p.removeBody(w, C), h.current && (s(!u), h.current = false), x(), delete m.indexInArray, c && m.collideBox && m.collideBox.removeFromParent(), delete P[w][m.indexInArray], delete m.collideBox;
        return;
      } else
        P[w][C] = b, n(), de(m, r[C], r[C]);
    }), () => {
      g.current = false, m.indexInArray !== void 0 && (p.removeBody(w, m.indexInArray), delete m.indexInArray, c && m.collideBox && m.collideBox.removeFromParent(), delete P[w][m.indexInArray], delete m.collideBox);
    };
  }, [e, i, r, u]), ie(m), m;
}, X = (e, t, r, i, o) => {
  const { workerApiRef: c } = /* @__PURE__ */ Y(K);
  return O(() => {
    if (!(i === null || isNaN(i)))
      return o && (I.physicEvent[`${t}_${r}_${i}`] = o, c.current.bindEventCB("attach", e, t, i)), () => {
        i === null || isNaN(i) || (c.current.bindEventCB("detach", e, t, i), delete I.physicEvent[`${t}_${r}_${i}`]);
      };
  }, [o, i]), null;
}, Ct = (e, t, r) => {
  if (!(e.indexInArray === null || isNaN(e.indexInArray)) && r.SAB)
    for (const i in r.SAB) {
      const o = r.SAB[i];
      t.current.setBodySAB("static", e.indexInArray, i, o.buffer);
    }
}, Ot = (e, t, r) => {
  if (e.indexInArray === null || isNaN(e.indexInArray))
    return;
  const i = e.indexInArray, o = t.current, c = I.physicEvent;
  r.onCollide && (c[`static_collide_${i}`] = e.onCollide, o.bindEventCB("attach", "binderOnCollide", "static", i)), r.onClick && (c[`static_click_${i}`] = e.onClick, o.bindEventCB("attach", "binderOnClick", "static", i)), r.onMouseOver && (c[`static_mouseover_${i}`] = e.onMouseOver, o.bindEventCB("attach", "binderOnMouseOver", "static", i)), r.onMouseLeave && (c[`static_mouseleave_${i}`] = e.onMouseLeave, o.bindEventCB("attach", "binderOnMouseLeave", "static", i)), r.onStopCollide && (c[`static_stop_collide_${i}`] = e.onStopCollide, o.bindEventCB("attach", "binderOnStopCollide", "static", i)), r.onCameraIntersect && (c[`static_camera_intersect_${i}`] = e.onCameraIntersect, o.bindEventCB("attach", "binderOnCameraIntersect", "static", i)), r.onPointerIntersect && (c[`static_pointer_intersect_${i}`] = e.onPointerIntersect, o.bindEventCB("attach", "binderOnPointerIntersect", "static", i));
}, Le = (e, t) => {
  if (e.indexInArray === null || isNaN(e.indexInArray))
    return;
  const r = e.indexInArray, i = t.current, o = I.physicEvent;
  i.bindEventCB("detach", "binderOnCollide", "static", r), delete o[`static_collide_${r}`], i.bindEventCB("detach", "binderOnClick", "static", r), delete o[`static_click_${r}`], i.bindEventCB("detach", "binderOnMouseOver", "static", r), delete o[`static_mouseover_${r}`], i.bindEventCB("detach", "binderOnMouseLeave", "static", r), delete o[`static_mouseleave_${r}`], i.bindEventCB("detach", "binderOnStopCollide", "static", r), delete o[`static_stop_collide_${r}`], i.bindEventCB("detach", "binderOnCameraIntersect", "static", r), delete o[`static_camera_intersect_${r}`], i.bindEventCB("detach", "binderOnPointerIntersect", "static", r), delete o[`static_pointer_intersect_${r}`];
}, Ke = new B.Matrix4(), St = (e, t = { entity: null, friction: 1, mass: 1, affectCamera: true, sleep: false, zoneIds: [0], zones: {}, essential: false, refreshCollide: false }, r) => {
  const [i, o, c, u] = /* @__PURE__ */ _((x) => [x.sharedStatic, x.sharedArrayReady, x.incTotalEssentialReady, x.debugPhysic]), [s, l] = /* @__PURE__ */ ee(false), h = /* @__PURE__ */ F(false), g = /* @__PURE__ */ F(false), f = /* @__PURE__ */ F(false), { workerApiRef: y, bodies: d, debugMaterial: m } = /* @__PURE__ */ Y(K), n = /* @__PURE__ */ L(() => {
    let x = e && e.hasOwnProperty("current") ? e.current : e;
    const v = { friction: 1, mass: 1, zones: {}, affectCamera: true, entity: x, zoneIds: [0], collideBox: null, toggleSleep: (P) => {
      v.sleep = P, !(v.indexInArray === null || isNaN(v.indexInArray)) && y.current.toggleBodyProp("static", v.indexInArray, "sleep", P);
    }, ...t };
    return v;
  }, [e]);
  return O(() => {
    n.toggleSleep(t.sleep);
  }, [t.sleep]), O(() => {
    n.side = t.side, !(n.indexInArray === null || isNaN(n.indexInArray)) && y.current.toggleSide("static", n.indexInArray, t.side);
  }, [t.side]), X("binderOnCollide", "static", "collide", n.indexInArray, t.onCollide), X("binderOnClick", "static", "click", n.indexInArray, t.onClick), X("binderOnMouseOver", "static", "mouseover", n.indexInArray, t.onMouseOver), X("binderOnMouseLeave", "static", "mouseleave", n.indexInArray, t.onMouseLeave), X("binderOnStopCollide", "static", "stop_collide", n.indexInArray, t.onStopCollide), X("binderOnCameraIntersect", "static", "camera_intersect", n.indexInArray, t.onCameraIntersect), X("binderOnPointerIntersect", "static", "pointer_intersect", n.indexInArray, t.onPointerIntersect), O(() => {
    let x = e && e.hasOwnProperty("current") ? e.current : e;
    const v = "static", P = d.current, p = y.current;
    if (!x || !o)
      return;
    if (h.current) {
      g.current = true;
      return;
    }
    const w = /* @__PURE__ */ G(x, null, m, v);
    return w.type = v, w.api = n, f.current = true, h.current = true, Be(p, v, w, n, { friction: n.friction, mass: n.mass }, { sleep: n.sleep }, n.side).then((b) => {
      if (h.current = false, !f.current || g.current) {
        p.removeBody(v, b), g.current && (l(!s), g.current = false), Le(n, y), delete n.indexInArray, u && n.collideBox && n.collideBox.removeFromParent(), delete P[v][n.indexInArray], delete n.collideBox;
        return;
      } else
        P[v][b] = w, Ot(n, y, t), Ct(n, y, t), de(n, i[b], i[b]), t.roles && y.current.toggleBodyProp("static", n.indexInArray, "roles", t.roles), t.noDepth && y.current.toggleBodyProp("static", n.indexInArray, "noDepth", t.noDepth), n.essential && c(1), r && r(n, p);
    }), () => {
      f.current = false, n.indexInArray !== void 0 && (p.removeBody(v, n.indexInArray), Le(n, y), delete n.indexInArray, u && n.collideBox && n.collideBox.removeFromParent(), delete P[v][n.indexInArray], delete n.collideBox);
    };
  }, [e, o, i, s]), O(() => {
    let x = e && e.hasOwnProperty("current") ? e.current : e;
    if (!x || !o || !n.collideBox || h.current)
      return;
    const v = "static", P = d.current, p = y.current;
    Ke.copy(n.collideBox.matrixWorldAtCreation);
    const w = /* @__PURE__ */ G(x, null, m, v);
    return w.type = v, w.api = n, n.collideBox = w, je(p, v, n.collideBox, n.indexInArray, { prevMatrixWorld: Ke.toArray(), matrixWorld: w.matrixWorldAtCreation.toArray() }), P[v][n.indexInArray] = w, () => {
      u && n.collideBox && n.collideBox.removeFromParent();
    };
  }, [t.refreshCollide]), ie(n), O(() => {
    n.indexInArray !== void 0 && (t.roles && y.current.toggleBodyProp("static", n.indexInArray, "roles", t.roles), t.noDepth && y.current.toggleBodyProp("static", n.indexInArray, "noDepth", t.noDepth));
  }, [n.indexInArray, t.roles, t.noDepth]), n;
}, Ft = (e) => e.x % (Math.PI / 2) !== 0 || e.y % (Math.PI / 2) !== 0 || e.z % (Math.PI / 2) !== 0, Rt = (e, t = { refreshCollide: false }) => {
  const [r, i, o] = /* @__PURE__ */ _((x) => [x.sharedZone, x.sharedArrayReady, x.debugPhysic]), [c, u] = /* @__PURE__ */ ee(false), s = /* @__PURE__ */ F(false), l = /* @__PURE__ */ F(false), h = /* @__PURE__ */ F(false), { workerApiRef: g, bodies: f, debugMaterial: y } = /* @__PURE__ */ Y(K), d = /* @__PURE__ */ L(() => {
    let x = e && e.hasOwnProperty("current") ? e.current : e;
    const v = { type: "zone", zones: {}, entity: x, zoneIds: [0], collideBox: null, inZone: false, sleep: false, toggleSleep: (P) => {
      v.sleep = P, !(v.indexInArray === null || isNaN(v.indexInArray)) && g.current.toggleBodyProp("zone", v.indexInArray, "sleep", P);
    }, ...t };
    return v;
  }, [e]);
  O(() => {
    d.toggleSleep(t.sleep);
  }, [t.sleep]);
  const m = () => {
    d.indexInArray === null || isNaN(d.indexInArray) || (t.onEnterZone && (I.physicEvent[`enter_zone_${d.indexInArray}`] = d.onEnterZone, g.current.attachEnterZoneCB(d.indexInArray)), t.onLeaveZone && (I.physicEvent[`leave_zone_${d.indexInArray}`] = d.onLeaveZone, g.current.attachLeaveZoneCB(d.indexInArray)));
  }, n = () => {
    d.indexInArray === null || isNaN(d.indexInArray) || (g.current.detachEnterZoneCB(d.indexInArray), delete I.physicEvent[`enter_zone_${d.indexInArray}`], g.current.detachLeaveZoneCB(d.indexInArray), delete I.physicEvent[`leave_zone_${d.indexInArray}`]);
  };
  return O(() => (d.onEnterZone = t.onEnterZone, d.onLeaveZone = t.onLeaveZone, m(), () => {
    n();
  }), [d.indexInArray, t.onEnterZone, t.onLeaveZone]), O(() => {
    let x = e && e.hasOwnProperty("current") ? e.current : e;
    const { type: v } = d, P = f.current, p = g.current;
    if (!x || !i)
      return;
    if (s.current) {
      l.current = true;
      return;
    }
    const w = /* @__PURE__ */ G(x, null, y, v);
    return w.type = v, w.api = d, h.current = true, s.current = true, Be(p, v, w, d, void 0, { sleep: true }, void 0, (b) => {
      if (Ft(x.rotation)) {
        const C = new B.Mesh(x.geometry);
        C.geometry.computeBoundingBox();
        const R = new B.Vector3();
        C.geometry.boundingBox.getSize(R), b.matrix = x.matrixWorld.elements, b.size = R.toArray();
      }
    }).then((b) => {
      if (s.current = false, !h.current || l.current) {
        p.removeBody(v, b), l.current && (u(!c), l.current = false), n(), delete d.indexInArray, o && w.removeFromParent(), delete d.collideBox, delete P[v][b];
        return;
      } else
        P[v][b] = w, m(), de(d, r[b], r[b]), d.toggleSleep(d.sleep);
    }), () => {
      h.current = false, d.indexInArray !== void 0 && (p.removeBody(v, d.indexInArray), delete d.indexInArray, o && d.collideBox && d.collideBox.removeFromParent(), delete P[v][d.indexInArray], delete d.collideBox);
    };
  }, [e, t.refreshCollide, i, r, c]), ie(d), [d];
};
new B.Vector3(), new B.Vector3();
const Tt = 50, ae = new B.Quaternion(), ue = new B.Quaternion();
function Et(e, t, r) {
  ae.x = e.prevQuaternionx, ae.y = e.prevQuaterniony, ae.z = e.prevQuaternionz, ae.w = e.prevQuaternionw, ue.x = e.quaternionx, ue.y = e.quaterniony, ue.z = e.quaternionz, ue.w = e.quaternionw, ae.slerp(ue, r), t.quaternion.copy(ae), t.position.x = B.MathUtils.lerp(e.prevPositionx, e.positionx, r), t.position.y = B.MathUtils.lerp(e.prevPositiony, e.positiony, r), t.position.z = B.MathUtils.lerp(e.prevPositionz, e.positionz, r);
}
const zt = (e) => {
  const t = /* @__PURE__ */ L(() => new B.Vector3(), []);
  $("resetFixedOffsetCamera", () => {
    const { playingAnimation: i, cinematicMode: o, dialogMode: c, forcedPosition: u, fixedCameraOffset: s, preventReset: l } = /* @__PURE__ */ V();
    if (i || u || l || o || c)
      return;
    const { sharedCamera: h } = /* @__PURE__ */ z();
    s.set(h.fixedCameraOffsetx, h.fixedCameraOffsety, h.fixedCameraOffsetz), t.set(0, 0, 0);
  });
  const r = 4;
  te((i, o) => {
    const { fixedCameraOffset: c } = /* @__PURE__ */ V();
    (parseFloat(c.x.toFixed(2)) !== parseFloat(t.x.toFixed(2)) || parseFloat(c.y.toFixed(2)) !== parseFloat(t.y.toFixed(2)) || parseFloat(c.z.toFixed(2)) !== parseFloat(t.z.toFixed(2))) && (c.damp(t, r, o), e.fixedCameraOffsetx = c.x, e.fixedCameraOffsety = c.y, e.fixedCameraOffsetz = c.z);
  });
}, D = new B.Vector3(), j = new B.Vector3(), Mt = (e) => {
  const [t, r] = /* @__PURE__ */ le((l) => [l.setCameraOffset, l.setTargetOffset]), [i, o, c, u] = /* @__PURE__ */ le((l) => [l.cameraOffsetDefault, l.defaultTargetOffset, l.birdView, l.cameraOffsetBirdView]);
  L(() => {
    D.set(c ? u.x : i.x, c ? u.y : i.y, c ? u.z : i.z), j.set(o.x, o.y, o.z);
  }, [u.x, u.y, u.z, i.x, i.y, i.z, o.x, o.y, o.z, c]), $("resetOffsetCamera", () => {
    const { playingAnimation: l, cinematicMode: h, dialogMode: g, forcedPosition: f, preventReset: y, birdView: d } = /* @__PURE__ */ V();
    l || y || f || h || g || (D.set(d ? u.x : i.x, d ? u.y : i.y, d ? u.z : i.z), j.set(o.x, o.y, o.z));
  });
  const s = 4;
  te((l, h) => {
    const { aimMode: g, cameraOffset: f, targetOffset: y } = /* @__PURE__ */ V();
    if ((parseFloat(f.x.toFixed(2)) !== parseFloat(D.x.toFixed(2)) || parseFloat(f.y.toFixed(2)) !== parseFloat(D.y.toFixed(2)) || parseFloat(f.z.toFixed(2)) !== parseFloat(D.z.toFixed(2))) && D.damp(f, s, h), (parseFloat(y.x.toFixed(2)) !== parseFloat(j.x.toFixed(2)) || parseFloat(y.y.toFixed(2)) !== parseFloat(j.y.toFixed(2)) || parseFloat(y.z.toFixed(2)) !== parseFloat(j.z.toFixed(2))) && j.damp(y, s, h), isNaN(j.x) || j.z === 0)
      debugger;
    if (isNaN(D.x) || D.z === 0)
      debugger;
    e.cameraOffsetx = D.x, g || (e.cameraOffsety = D.y), e.cameraOffsetz = D.z, e.targetOffsetx = j.x, e.targetOffsety = j.y, e.targetOffsetz = j.z, t([e.cameraOffsetx, e.cameraOffsety, e.cameraOffsetz]), r([e.targetOffsetx, e.targetOffsety, e.targetOffsetz]);
  });
}, kt = () => {
  k("cinematicEvent", { open: false }), k("cinematicText", { open: false }), k("cinematic", false);
  const { refCamera: e, defaultFov: t } = /* @__PURE__ */ V();
  e.current && (e.current.fov = t, e.current.updateProjectionMatrix()), H({ cinematicMode: false });
};
let U = function() {
}, Qe = setTimeout(() => {
});
const { damp: _t } = B.MathUtils, Vt = (e) => new Promise((t, r) => {
  Qe = setTimeout(function() {
    U = function() {
    }, t();
  }, e), U = function() {
    clearTimeout(Qe), r();
  };
}), Nt = (e) => {
  const [t, r] = /* @__PURE__ */ ee(() => function() {
  }), i = /* @__PURE__ */ F({});
  $("escapeCtrl", () => {
    H({ interruptCinematic: true }), U && typeof U == "function" && U(), t && typeof t == "function" && t();
  }), $("skip", () => {
    i.current.preventSkip || U && typeof U == "function" && U();
  }), $("cinematic", (o) => {
    e && typeof e == "function" && e(o);
  }), $("cinematicCamera", ({ sequences: o, onCinematicEnd: c = function() {
  }, onCinematicCancel: u = function() {
  } }) => {
    const s = function() {
      r(null), c();
    };
    r(() => function() {
      u(), r(null);
    }), H({ interruptCinematic: false }), (async (h) => {
      for (let g = 0; g < o.length; g++) {
        const f = o[g];
        i.current = f;
        const { cinematicPositionTo: y, cinematicQuaternionTo: d, cinematicFov: m, refCamera: n, interruptCinematic: x } = /* @__PURE__ */ V();
        if (x) {
          H({ interruptCinematic: false });
          break;
        }
        f.fovFrom = f.fovFrom ?? 50, n.current.position.fromArray(f.posFrom), n.current.quaternion.fromArray(f.quatFrom), n.current.fov = f.fovFrom, n.current.updateProjectionMatrix(), f.cinematicText && k("cinematicText", { name: f.name ?? "", message: f.cinematicText }), k("cinematicEvent", {}), k("cinematic", !f.withoutBorder), H({ cinematicMode: true, cinematicPositionTo: y.fromArray(f.posTo ?? f.posFrom), cinematicQuaternionTo: d.fromArray(f.quatTo ?? f.quatFrom), cinematicFov: f.fovTo ?? m, cinematicDamp: f.dampSpeed ?? 1 }), await Vt(f.duration * 1e3).catch((v) => {
        }), f.endSequenceCb && f.endSequenceCb();
      }
      kt(), s();
    })();
  });
}, qt = (e, t) => {
  const { cinematicPositionTo: r, cinematicQuaternionTo: i, cinematicFov: o, cinematicDamp: c } = /* @__PURE__ */ V();
  e.current.position.damp(r, c, t), e.current.quaternion.damp(i, c, t), e.current.fov = _t(e.current.fov, o, c, t), e.current.updateProjectionMatrix();
}, Ye = /* @__PURE__ */ J.forwardRef(({ makeDefault: e, children: t, ...r }, i) => {
  const o = /* @__PURE__ */ me(({ set: l }) => l), c = /* @__PURE__ */ me(({ camera: l }) => l), u = /* @__PURE__ */ me(({ size: l }) => l), s = /* @__PURE__ */ J.useRef(null);
  return J.useLayoutEffect(() => {
    r.manual || (s.current.aspect = u.width / u.height);
  }, [u, r]), J.useLayoutEffect(() => {
    s.current.updateProjectionMatrix();
  }), J.useLayoutEffect(() => {
    if (e) {
      const l = c;
      return o(() => ({ camera: s.current })), () => o(() => ({ camera: l }));
    }
  }, [s, e, o]), J.useImperativeHandle(i, () => s.current), W(Oe, { children: W("perspectiveCamera", { ref: s, ...r, children: t }) });
});
Ye.displayName = "PerspectiveCamera";
const oe = new B.Quaternion(), fe = new B.Quaternion(), Dt = ({ targetRef: e, children: t, withCinematic: r = false, cinematicEventCb: i, invertX: o = true, invertY: c = false, autoTilt: u = true }) => {
  const [s, l, h, g, f] = /* @__PURE__ */ le((P) => [P.editor, P.control, P.orbitCtrl, P.renderPriority, P.defaultFov]), y = /* @__PURE__ */ L(() => new B.Vector3(), []), d = /* @__PURE__ */ F(null), m = /* @__PURE__ */ F(null), n = /* @__PURE__ */ F(null), x = /* @__PURE__ */ _((P) => P.sharedCamera), v = /* @__PURE__ */ Ze((P) => {
    d.current = P, H({ refCamera: d });
  });
  return O(() => {
    x && (x.dragDamp = 16, x.targetHeight = 1);
  }, [x]), O(() => {
    x && (x.autoTilt = u);
  }, [x, u]), O(() => {
    x && (x.invertX = o, x.invertY = c);
  }, [x, o, c]), r && Nt(i), Mt(x), zt(x), te((P, p) => {
    if (!(e && e.current || e))
      return;
    const { cinematicMode: b } = /* @__PURE__ */ V();
    if (b) {
      qt(d.current, p);
      return;
    }
    l.current && (l.current.target = y);
  }, g), $("readPhysicState", () => {
    if (!x || x.prevPositionx === void 0)
      return;
    const { cinematicMode: P } = /* @__PURE__ */ V();
    if (P)
      return;
    const p = d;
    p.current && (p.current.position.x = B.MathUtils.lerp(x.prevPositionx, x.positionx, I.interpolationFactor), p.current.position.y = B.MathUtils.lerp(x.prevPositiony, x.positiony, I.interpolationFactor), p.current.position.z = B.MathUtils.lerp(x.prevPositionz, x.positionz, I.interpolationFactor), oe.x = x.prevQuaternionx, oe.y = x.prevQuaterniony, oe.z = x.prevQuaternionz, oe.w = x.prevQuaternionw, fe.x = x.quaternionx, fe.y = x.quaterniony, fe.z = x.quaternionz, fe.w = x.quaternionw, oe.slerp(fe, I.interpolationFactor), p.current.quaternion.copy(oe), p.current.updateMatrix(), p.current.updateWorldMatrix(false, false), p.current.updateMatrixWorld(true));
  }), Ce(Oe, { children: [W("object3D", { ref: n, matrixAutoUpdate: true, children: W("object3D", { ref: m, matrixAutoUpdate: true }) }), W(Ye, { directionVector: new B.Vector3(0, 0, -1), directionToVector: new B.Vector3(0, 0, 0), makeDefault: !h, ref: v, fov: f, far: 3500, matrixAutoUpdate: true, children: t })] });
}, jt = new B.Euler(), M = new B.Quaternion(), be = new B.Vector3(), ye = new B.Vector3(), Lt = new B.Vector3(0, 1, 0), Kt = (e, t) => {
  const { playerApi: r, halfCapsule: i, sharedCamera: o } = /* @__PURE__ */ z();
  if (I.sharedPhysic) {
    if (I.sharedPhysic.physicReadingTempArray) {
      let u = performance.now(), s = true;
      for (; I.sharedPhysic.physicReadingTempArray && s; )
        s = performance.now() - u < 500;
    }
    I.sharedPhysic.frontWritingTempArray = true;
  }
  r.localVelocityx = 0, r.localVelocityy = 0, r.localVelocityz = 0, r.externalVelocityx = 0, r.externalVelocityy = 0, r.externalVelocityz = 0, r.forcex = 0, r.forcey = 0, r.forcez = 0, r.orbital = false, r.flying = false, r.driving = false, r.sliding = false, r.jumping = false, r.attractorx = 0, r.attractory = 1, r.attractorz = 0, t ? (M.fromArray(t).normalize(), r.quaternionx = M.x, r.quaterniony = M.y, r.quaternionz = M.z, r.quaternionw = M.w) : M.set(r.quaternionx, r.quaterniony, r.quaternionz, r.quaternionw), be.copy(Lt).applyQuaternion(M), be.multiplyScalar(-i), ye.set(e[0], e[1], e[2]), ye.add(be), r.positionx = ye.x, r.positiony = ye.y, r.positionz = ye.z;
  const { lockRotation: c } = /* @__PURE__ */ V();
  if (r.entity.position.set(e[0], e[1], e[2]), r.entity.quaternion.copy(M), !c) {
    const u = /* @__PURE__ */ jt.setFromQuaternion(r.entity.quaternion);
    u.reorder("YXZ"), o.transformerYTotalRot = u.y, o.absoluteDeltax = B.MathUtils.radToDeg(u.y), o.absoluteDeltaNoLerpx = B.MathUtils.radToDeg(u.y), o.angleOffset = 0, o.prevAngleOffset = 0, M.identity(), o.nextQuaternionTotalYx = M.x, o.nextQuaternionTotalYy = M.y, o.nextQuaternionTotalYz = M.z, o.nextQuaternionTotalYw = M.w, o.forcequaternionTotalY = true;
  }
  I.sharedPhysic.frontWritingTempArray = false, o.disableCameraPositionLerp || (o.disableCameraPositionLerp = true, I.runOnceAfterOnePhysicFrame(() => {
    o.disableCameraPositionLerp = false;
  }));
};
export {
  q as CAMERA_MODES,
  xt as Physics,
  Dt as ThirdPerson,
  ve as capsuleLength,
  Ae as capsuleRadius,
  z as getPhysic,
  V as getThirdPersonStore,
  Tt as groupingBoxSize,
  Et as interpolateEntity,
  yt as offsetScalerMax,
  ft as offsetScalerMin,
  ke as oniState,
  I as physicApi,
  K as physicContext,
  ne as setPhysic,
  H as setThirdPersonStore,
  Kt as teleportPlayer,
  wt as useDynamic,
  At as useInReach,
  bt as useKinematic,
  Re as usePageVisibility,
  It as usePeerKinematic,
  _ as usePhysicStore,
  St as useStatic,
  le as useThirdPersonStore,
  Rt as useZone
};
